/* =====================================================================================
   Second-factor authentication.

   Replaces a WebAuthn ceremony that ran entirely in the browser. That version could not
   succeed — nothing ever enrolled a passkey, so there was no credential to find — and even
   when it appeared to work it proved nothing, because the server never saw it. Any user
   could have skipped it from the console.

   Supabase TOTP is different in the way that matters: verifying a factor causes Supabase to
   REISSUE THE SESSION TOKEN with aal = 'aal2' and an amr entry timestamped to the second.
   Both sit inside a signed JWT, so app.step_up_fresh() can trust them and the check happens
   where the data is rather than where the buttons are.

   TOTP rather than passkeys because Supabase verifies TOTP server-side and does not verify
   WebAuthn at all. A passkey flow would need a verifier we would have to build and host, and
   an unverified passkey is decoration.
   ===================================================================================== */

import { BACKEND } from './db';
import { extractSvg, normaliseSecret } from './qr';

export { extractSvg, addViewBox, renderQr, qrImageSrc, normaliseSecret } from './qr';

export interface Factor {
  id: string;
  friendly_name?: string;
  factor_type: string;
  status: 'verified' | 'unverified';
}

export interface Assurance {
  currentLevel: string | null;
  nextLevel: string | null;
  /** True when a second factor is enrolled and this session has NOT yet presented it. */
  needsChallenge: boolean;
  enrolled: boolean;
}

const live = () => BACKEND === 'supabase';

export async function listFactors(): Promise<Factor[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().auth.mfa.listFactors();
  if (error) throw new Error(error.message);
  return [...(data?.totp ?? []), ...((data as never as { phone?: Factor[] })?.phone ?? [])] as Factor[];
}

/*
 * Every factor, including the unverified ones.
 *
 * listFactors() above cannot be used for cleanup: auth-js buckets a factor into `totp` only
 * when its status is already 'verified' (GoTrueClient._listFactors), so abandoned enrolment
 * attempts are invisible through it. They are present on `all`, and on the user object,
 * which is where the enrolment path has to look.
 */
export async function allFactors(): Promise<Factor[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().auth.mfa.listFactors();
  if (error) throw new Error(error.message);
  const all = (data as never as { all?: Factor[] })?.all;
  if (all) return all;
  // Older clients had no `all`; read the user's factor list directly.
  const { data: u } = await supabase().auth.getUser();
  return ((u?.user as never as { factors?: Factor[] })?.factors ?? []) as Factor[];
}

export async function assurance(): Promise<Assurance> {
  if (!live()) {
    return { currentLevel: null, nextLevel: null, needsChallenge: false, enrolled: false };
  }
  const { supabase } = await import('./supabase');
  const { data } = await supabase().auth.mfa.getAuthenticatorAssuranceLevel();
  const factors = await listFactors().catch(() => []);
  const enrolled = factors.some((f) => f.status === 'verified');
  return {
    currentLevel: data?.currentLevel ?? null,
    nextLevel: data?.nextLevel ?? null,
    // nextLevel above currentLevel means Supabase knows a factor exists and this session has
    // not used it yet.
    needsChallenge: Boolean(data && data.nextLevel === 'aal2' && data.currentLevel !== 'aal2'),
    enrolled,
  };
}

export interface Enrolment {
  factorId: string;
  qrSvg: string | null;
  secret: string | null;
  uri: string | null;
}

/**
 * Begin enrolment. Returns a QR code to scan. The factor stays UNVERIFIED until a code from
 * the authenticator is confirmed, so an abandoned enrolment cannot lock anybody out.
 */
export async function beginEnrol(friendlyName = 'Authenticator'): Promise<Enrolment> {
  const { supabase } = await import('./supabase');

  /* Clear abandoned attempts first.
   *
   * Every call to enroll() creates a factor. Close the dialog before entering a code, or
   * enter a wrong one, and that factor stays behind in `unverified` state holding the name.
   * The next attempt then fails with "a factor with this friendly name already exists" and
   * the person is stuck, having done nothing wrong.
   *
   * Deleting unverified factors is safe by definition: a factor nobody has ever proved
   * possession of grants no access and protects nothing. Verified factors are left alone. */
  const existing = await allFactors();
  for (const f of existing) {
    if (f.status !== 'verified') {
      try { await supabase().auth.mfa.unenroll({ factorId: f.id }); } catch { /* best effort */ }
    }
  }

  const { data, error } = await supabase().auth.mfa.enroll({
    factorType: 'totp', friendlyName,
  });
  if (error) {
    if (/already exists|friendly.?name/i.test(error.message)) {
      // Only reachable now if a VERIFIED factor holds the name, which is a real conflict.
      throw new Error(
        'This device name is already registered and verified. Remove it below, or use a different name.',
      );
    }
    throw new Error(error.message);
  }
  return {
    factorId: data.id,
    qrSvg: extractSvg(data.totp?.qr_code ?? null),
    // Base32 is A-Z and 2-7 only. Anything else, including the '=' padding some libraries
    // add and any lowercase, is rejected by authenticator apps as an illegal character.
    secret: normaliseSecret(data.totp?.secret ?? null),
    uri: data.totp?.uri ?? null,
  };
}


/** Confirm enrolment with a code from the app. Also raises this session to aal2. */
export async function confirmEnrol(factorId: string, code: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
  if (error) throw new Error(readable(error.message));
}

export async function removeFactor(factorId: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message);
}

/**
 * Step up: present the second factor now. On success Supabase reissues the token with
 * aal2, which is what app.step_up_fresh() reads. Nothing here decides access; it obtains
 * the evidence and the database decides.
 */
export async function stepUp(code: string): Promise<void> {
  const { supabase } = await import('./supabase');
  const factors = await listFactors();
  const totp = factors.find((f) => f.status === 'verified');
  if (!totp) throw new Error('no-factor');

  const { error } = await supabase().auth.mfa.challengeAndVerify({
    factorId: totp.id, code: code.trim(),
  });
  if (error) throw new Error(readable(error.message));
}

/** Ask the database what this session actually holds, rather than trusting the client. */
export async function serverAssurance(): Promise<{ aal: string; steppedUp: boolean } | null> {
  if (!live()) return null;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('session_assurance');
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return row ? { aal: row.out_aal, steppedUp: Boolean(row.out_stepped_up) } : null;
}

function readable(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('invalid') && m.includes('code')) {
    return 'That code was not accepted. Codes change every 30 seconds, so try the current one.';
  }
  if (m.includes('expired')) return 'That code has expired. Enter the current one.';
  if (m.includes('rate')) return 'Too many attempts. Wait a minute and try again.';
  return message;
}
